import {
	DdiziInfoType,
	OnlineTicketType,
	VaunceAppV2AppTicketUserType,
	VaunceAppV2AppWeekdayAvailable,
} from '@kinderlabs-pos/shared-data-type';

export const VaunceAppV2AppTicketTypeConst = [
	'ONE_TIME',
	'MULTI_USE',
	'COMMUTATION',
	'NORMAL_PRODUCT',
	'PACKAGE',
	'ONLINE',
] as const;
export type VaunceAppV2AppTicketType = (typeof VaunceAppV2AppTicketTypeConst)[number];
export const VaunceAppV2AppTicketTypeEnum: Record<VaunceAppV2AppTicketType, string> = {
	ONE_TIME: '일회권',
	MULTI_USE: '다회권',
	COMMUTATION: '정기권',
	NORMAL_PRODUCT: '일반상품',
	PACKAGE: '패키지',
	ONLINE: '온라인',
};

export type TicketDetailRoot = {
	createdDate: Date;
	modifiedDate: Date;
	createdBy: string;
	modifiedBy: string;
	//
	id: number;
	storeId: number;
	//
	storeVisitMainMemberInfo: StoreVisitMainMemberInfo;
	storeVisitStatus: string;
	//
	completedAt?: Date;
	canceledAt?: Date;
	//
	storeVisitMemberWithTicketList: StoreVisitMemberWithTicketList[];
};

export type MemberRootDetail = {
	id: number;

	//
	storeVisitMemberList: {
		username: string;
		birthday: string;
		gender: string;
		ticketId: number;
		ticketType: VaunceAppV2AppTicketType;
		ticketName: string;
		packageChildId?: number;
		mutChildId?: number;
		packageChildTicketName?: string;
		onlineTicketId?: string;
	}[];
	storeVisitMainMemberInfo: StoreVisitMainMemberInfo;
};

export type StoreVisitMainMemberInfo = {
	memberId: number;
	username: string;
	telephone: string;
	gender: 'MALE' | 'FEMALE' | 'UNKNOWN';
};

export type PosOrderInfo = {
	orderId: string;
	posId: number;
	amount: number;
	totalTicketCount: number;
	parentsTicketCount: number;
	normalTicketCount: number;
	kidsTicketCount: number;
};

export type StoreVisitMemberList = {
	id: number;
	username: string;
	birthday: string;
	gender: string;
	telephone: string;
	ticketUser: 'NORMAL' | 'PARENTS' | 'KIDS';
	ticketType: VaunceAppV2AppTicketType;
	ticketName: string;
	ticketId: number;
	mutChildId?: number;
	storeVisitId: number;
};

// App-BE TicketStatus enum 과 동일. 환불 건은 BE 에서 UNUSABLE 로 내려온다.
export const ticketStatusConst = ['USABLE', 'COMPLETED', 'UNUSABLE', 'EXPIRED', 'GIFTED'] as const;
export type TicketStatus = (typeof ticketStatusConst)[number];
export const TicketStatusEnum: Record<TicketStatus, string> = {
	USABLE: '사용가능',
	COMPLETED: '사용완료',
	UNUSABLE: '환불/비활성',
	EXPIRED: '만료',
	GIFTED: '선물함',
};
// ONLINE 티켓은 ticketStatus 가 null 일 수 있다. USABLE 이 아니면 모두 발권 불가로 본다.
export const isTicketIssuable = (ticket: { ticketStatus: TicketStatus | null }) =>
	ticket.ticketStatus === 'USABLE';
export const getNotIssuableLabel = (ticket: { ticketStatus: TicketStatus | null }) =>
	`발권불가 (${ticket.ticketStatus ? TicketStatusEnum[ticket.ticketStatus] : '사용불가'})`;
export type StoreVisitMemberWithTicketList = {
	ticketId: number;
	ticketName: string;
	issuedAt: Date;
	expiration: Date;
	//
	isUsed: boolean;
	isActive: boolean;
	productId: number;
	//
	ticketStatus: TicketStatus | null;
	//
} & (
	| {
			ticketType: 'PACKAGE';
			pkgItems: PkgItem[];
	  }
	| {
			ticketType: 'COMMUTATION';
			ticketUser: 'NORMAL' | 'PARENTS' | 'KIDS';
			weekdayAvailable: VaunceAppV2AppWeekdayAvailable;
			ticketMinutes: number;
			cmtUserInfo: {
				birthday: string;
				imageUrl: string;
				username: string;
				gender: 'MALE' | 'FEMALE' | 'UNKNOWN';
			};
	  }
	| {
			ticketType: 'ONLINE';
			ticketUser: 'NORMAL' | 'PARENTS' | 'KIDS';
			externalOnlineTicketId?: null;
			ticketMinutes: number;
			onlineTicketDetailForPosAndKiosk: {
				ticketId: number;
				ticketPrice: number;
				ticketChannel: string;
				ticketType: OnlineTicketType;
				ticketMinutes: number;
				weekdayAvailable: VaunceAppV2AppWeekdayAvailable;

				onoffmixTicketId: string;
				onoffmixOrderId: string;
				onoffmixTicketInfoId: string;
				ddiziInfo: DdiziInfoType;
			};
	  }
	| {
			ticketType: 'MULTI_USE';
			ticketUser: 'NORMAL' | 'PARENTS' | 'KIDS';
			weekdayAvailable: VaunceAppV2AppWeekdayAvailable;
			ticketMinutes: number;
			mutChildren: {
				id: number;
				barcodeKey: string;
				isUsed: boolean;
				isActive: boolean;
			}[];
	  }
	| {
			ticketType: 'ONE_TIME';
			ticketUser: 'NORMAL' | 'PARENTS' | 'KIDS';
			weekdayAvailable: VaunceAppV2AppWeekdayAvailable;
			ticketMinutes: number;
	  }
);

export type PkgItem = {
	id: number;
	productId: number;
	name: string;
} & (
	| {
			ticketType: 'ONE_TIME';
			ticketUser: 'NORMAL' | 'PARENTS' | 'KIDS';
			weekdayAvailable: VaunceAppV2AppWeekdayAvailable;
			ticketMinutes: number;
	  }
	| {
			ticketType: 'NORMAL_PRODUCT';
	  }
);

//

export type StoreVisitMemberRoot = {
	createdDate: Date;
	modifiedDate: Date;
	createdBy: string;
	modifiedBy: string;
	//
	id: number;
	storeId: number;
	//
	storeVisitMainMemberInfo: StoreVisitMainMemberInfo;
	storeVisitStatus: string;
	//
	completedAt?: Date;
	canceledAt?: Date;
	//
	storeVisitMemberList: StoreVisitMember[];
};

export type StoreVisitMember = {
	id: number;
	username: string;
	birthday: string;
	gender: string;
	telephone: string;
	//
	ticketUser: VaunceAppV2AppTicketUserType;
	ticketName: string;
	ticketId: number;
} & (
	| {
			ticketType: 'MULTI_USE';
			mutChildId?: number;
	  }
	| {
			ticketType: 'COMMUTATION';
	  }
	| {
			ticketType: 'ONE_TIME';
	  }
);
// | {
// 		ticketType: 'ONE_TIME';
//   }
